import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren
} from '@angular/core';
import { stringSearch } from '../../utils/string.utils';
import { Tenant } from '../../models/tenant';
import { KEY_CODE } from '../../constants/keycodes';
import { HcTableDataSource, SearchBarComponent } from '@healthcatalyst/cashmere';
import { TenantService } from 'src/app/core/services/application/tenant.service';
import { ToastNotificationService } from 'src/app/core/services/toast-notification.service';
import { TenantContextService } from 'src/app/shared/tenant-context/tenant-context.service';
import { Observable, filter } from 'rxjs';
import { OktaAuthStateService } from '@okta/okta-angular';
import { AuthState } from '@okta/okta-auth-js';
import BaseComponent from '../base.component';
import { TenantFavsService } from '../../services/tenant-favs.service';
import { OktaAuthService } from 'src/app/core/services/http/okta-auth.service';

export interface TenantListItemVM extends Tenant {
    isFavorite: boolean;
}

@Component({
    selector: 'cdp-tenant-switcher',
    templateUrl: './tenant-switcher.component.html',
    styleUrls: ['./tenant-switcher.component.scss'],
    standalone: false
})
export class TenantSwitcherComponent extends BaseComponent implements OnInit {
    get tenantCode(): string {
        return this.tenantContextService.currentTenant?.tenantId || '';
    }
    @Input() isInDropdown = true;
    @Output() tenantSelected = new EventEmitter<Tenant>();
    @ViewChild('tenantSearch') searchBar!: SearchBarComponent;
    @ViewChildren('tenantRow') tenantRows!: QueryList<ElementRef>;
    tenantsDataSource = new HcTableDataSource<TenantListItemVM>();
    favTenantsDataSource = new HcTableDataSource<TenantListItemVM>();
    loadingTenants = false;
    loadingTenantsFailed = false;
    private loggedInUser$!: Observable<AuthState>;

    constructor(
        private tenantContextService: TenantContextService,
        private tenantService: TenantService,
        private toast: ToastNotificationService,
        private _oktaAuthStateService: OktaAuthStateService,
        private tenantFavs: TenantFavsService,
        private _oktaAuth: OktaAuthService
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        this.loggedInUser$ = this._oktaAuthStateService.authState$.pipe(
            filter((authState: AuthState) => !!authState && !!authState.isAuthenticated)
        );

        this.loggedInUser$.pipe(this.unsubscribeOnDestroy()).subscribe(() => {
            this.loadTenantList();
            this.tenantService.tenantCacheCleared$.pipe(this.unsubscribeOnDestroy()).subscribe(() => {
                this.loadTenantList();
            });
            this.isGlobalAdmin = this._oktaAuth.isGlobalAdmin();
        });

        this.tenantFavs.favsUpdated$.pipe(this.unsubscribeOnDestroy()).subscribe(async () => {
            await this.loadTenantList();
            this.search(this.searchBar.value);
        });

        setTimeout(() => {
            this.searchBar?.focus();
        });
    }

    async loadTenantList(toastOnError = false): Promise<void> {
        this.loadingTenants = true;
        try {
            const tenants = await this.tenantService.getTenants();
            const tenantVMs = this.createTenantListItemVMs(tenants);
            this.tenantsDataSource = new HcTableDataSource(tenantVMs);
            this.favTenantsDataSource = new HcTableDataSource(tenantVMs.filter(t => t.isFavorite));
            this.tenantsDataSource.filterPredicate = this.tenantFilterPredicate.bind(this);
            this.loadingTenantsFailed = false;
            this._handleSingleTenant();
        } catch (e) {
            console.error(`Failed to load tenants: ${e}`);
            this.tenantsDataSource = new HcTableDataSource(new Array<TenantListItemVM>());
            this.favTenantsDataSource = new HcTableDataSource(new Array<TenantListItemVM>());
            this.loadingTenantsFailed = true;
            if (toastOnError) {
                this.toast.showError('Contact an admin if the problem continues.', 'Failed to Load Tenants');
            }
        } finally {
            this.loadingTenants = false;
        }
    }

    private createTenantListItemVMs(tenants: Tenant[]): TenantListItemVM[] {
        const favs = this.tenantFavs.getTenantFavs();
        return tenants.map(tenant => {
            return {
                ...tenant,
                isFavorite: favs.includes(tenant.tenantId)
            };
        });
    }

    clearSearch(): void {
        this.searchBar.clear();
        this.tenantsDataSource.filter = '';
        this.favTenantsDataSource.filter = '';
    }

    search(term: string): void {
        this.tenantsDataSource.filter = term;
        this.favTenantsDataSource.filter = term;
    }

    async selectTenant(tenant: Tenant, showSuccessToast = true): Promise<void> {
        if (this.tenantContextService.currentTenant?.tenantId === tenant.tenantId) {
            return;
        }
        await this.tenantContextService.setTenantContext(tenant.tenantId);
        this.tenantSelected.emit(this.tenantsDataSource.data.find(t => t.tenantId === tenant.tenantId));
        if (showSuccessToast) {
            this.toast.showSuccess(`Switched to ${tenant.name}.`, 'Selected Tenant');
        }
    }

    onSearchKeydown(event: KeyboardEvent): void {
        if (event.which === KEY_CODE.DOWN_ARROW) {
            this.tenantRows.first?.nativeElement.focus();
        }
    }

    onTenantRowKeydown(event: KeyboardEvent, tenant: Tenant): void {
        if (event.which !== KEY_CODE.TAB) {
            event.preventDefault();
        }
        switch (event.which) {
            case KEY_CODE.ENTER:
                this.selectTenant(tenant);
                break;
            case KEY_CODE.DOWN_ARROW:
                this.shiftFocusedTenantRow(event);
                break;
            case KEY_CODE.UP_ARROW:
                this.shiftFocusedTenantRow(event, false);
                break;
            default:
                return;
        }
    }

    toggleFavTenant(tenant: TenantListItemVM, event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
        tenant.isFavorite = !tenant.isFavorite;
        if (tenant.isFavorite) {
            this.tenantFavs.addTenantFav(tenant.tenantId);
        } else {
            this.tenantFavs.removeTenantFav(tenant.tenantId);
        }
    }

    private shiftFocusedTenantRow(event: KeyboardEvent, shiftFocusDown = true) {
        const rows = this.tenantRows.toArray();
        const currentIndex = rows.findIndex(el => el.nativeElement === event.target);
        const maxIndex = rows.length - 1;
        let focusIndex = currentIndex + (shiftFocusDown ? 1 : -1);
        // pressing down key on last row jumps you back to top, pressing up key on first row jumps you to bottom
        focusIndex = focusIndex < 0 ? maxIndex : focusIndex > maxIndex ? 0 : focusIndex;
        rows[focusIndex]?.nativeElement.focus();
    }

    private tenantFilterPredicate(data: Tenant, filter: string): boolean {
        const stringToSearch = `${data.name} ${data.tenantId}`;
        return stringSearch(stringToSearch, filter);
    }

    private _handleSingleTenant(): void {
        if (this.tenantsDataSource.data.length === 1) {
            this.selectTenant(this.tenantsDataSource.data[0], false);
        }
    }
}
